/**
 * Southeast craft malthouses — Carolina Malt House, Epiphany Craft Malt,
 * Riverbend Malt House and South Fork Malthouse.
 *
 * Riverbend `southern-select` keeps its original id even though the product is
 * a pale ale malt rather than a pilsner; the name and class are corrected in
 * place so recipes that already snapshot that id still resolve. The same
 * applies to the two renamed Carolina Malt House rows below.
 *
 * Carolina Malt House and Murphy & Rude Malting merged operations in June 2026
 * and every Murphy & Rude malt is now made at Cleveland, North Carolina — but
 * the two catalogues stayed distinct (Murphy & Rude keep Virginia Pils, English
 * Pale, five crystals and eight dry roasts; Carolina Malt keep the base and
 * high-kilned line). Murphy & Rude therefore stays in `northeast.ts` and the
 * rows here are Carolina Malt products only.
 *
 * Carolina Malt House publish product names, usage ceilings and tasting notes
 * but no colour figures anywhere — not on their site, not through their
 * retailers. Their colours below are class-typical, which is exactly what the
 * default `representative` provenance claims; only the names, the flavour
 * character and the usage ceilings come off their own pages.
 */

import type { Fermentable } from '../../../schema/fermentable';
import { craftMalt } from './helpers';

/** North Carolina and Kentucky craft malts. @see Fermentable */
export const southeastCraftFermentables: readonly Fermentable[] = [
  // ---------------------------------------------------------------------------
  // Carolina Malt House — Cleveland, North Carolina
  // ---------------------------------------------------------------------------
  // `carolina-malt-carolina-pilsner` keeps its id; the sack says "Pilsner".
  craftMalt({
    id: 'carolina-malt-carolina-pilsner',
    name: 'Carolina Pilsner',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Light straw in the glass and clean on the palate, with the conversion power to carry a whole grist. The house lager base, and mild enough that Carolina Malt point it at ales as readily as lagers.',
    tags: ['base', 'pilsner', 'craft-malt', 'north-carolina', 'local'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/pilsner',
  }),
  // Was filed as a generic "Pale Malt"; the product on the sack is Carolina
  // Gold, and 2.5 °L is the only 2-row in their range that colour fits.
  craftMalt({
    id: 'carolina-malt-pale',
    name: 'Carolina Gold',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2.5,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The flagship single-variety 2-row and a 2019 Malt Cup bronze medallist. Golden and full-bodied with a clean, gently sweet finish, and enough enzyme to be the whole grist of a pale ale or an all-malt mash bill.',
    tags: ['base', 'two-row', 'craft-malt', 'north-carolina', 'local'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/project-title-5',
  }),
  craftMalt({
    id: 'carolina-malt-tennessee-2-row',
    name: 'Tennessee 2-Row',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Single-farm barley from Coffee County, Tennessee, malted pale and bright. Warm bread and fresh biscuit with a hint of graham cracker, clean enough to carry a Helles and soft enough for a hazy IPA.',
    tags: ['base', 'two-row', 'craft-malt', 'tennessee', 'single-farm'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/tennessee-two-row',
  }),
  craftMalt({
    id: 'carolina-malt-vienna',
    name: 'Vienna',
    maker: 'Carolina Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Honey and biscuit with a nutty edge, kilned warm but not far enough to lose its enzyme. Carolina Malt rate it to 100 %, so it will carry a malt-forward lager on its own or lift a paler grist a shade.',
    tags: ['kilned', 'vienna', 'amber', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/vienna',
  }),
  craftMalt({
    id: 'carolina-malt-light-munich',
    name: 'Light Munich',
    maker: 'Carolina Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Fresh bread and wild honey over an amber cast, still self-converting at full grist strength. The obvious base for a helles bock, and a few percent adds depth to anything paler.',
    tags: ['kilned', 'munich', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/light-munich',
  }),
  craftMalt({
    id: 'carolina-malt-dark-munich',
    name: 'Dark Munich',
    maker: 'Carolina Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 25,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Toffee, toasted bread and a frank malt sweetness, taken far enough up the kiln to carry real color. Built for dunkel, bock, schwarzbier and märzen, where it can be most of the grist rather than a seasoning.',
    tags: ['kilned', 'munich', 'dark', 'toffee', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/2-row-malts/dark-munich',
  }),
  craftMalt({
    id: 'carolina-malt-chit',
    name: 'Chit Malt',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 2,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 10,
    needsMashing: true,
    description:
      'NC-grown Violetta barley pulled out of germination early and kilned light, so the protein survives intact. Carolina Malt sell it as a dextrin-malt replacement for mouthfeel, foam and haze stability, at up to a tenth of the mash bill.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/adjunct-malts/chit-malt',
  }),
  craftMalt({
    id: 'carolina-malt-red-wheat',
    name: 'Malted Wheat',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 2.6,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Pale and malty-sweet with honey and a walnut note underneath. The protein does the usual wheat work — creamy body, foam that lasts — and rice hulls help the lauter once it passes a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'north-carolina', 'head-retention'],
    url: 'https://www.carolinamalt.com/portfolio-collections/adjunct-malts/malted-wheat',
  }),
  craftMalt({
    id: 'carolina-malt-malted-oats',
    name: 'Malted Oats',
    maker: 'Carolina Malt House',
    type: 'adjunct-grain',
    maltClass: 'wheat',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 2,
    diastaticPowerLintner: 25,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Gerard 224 oats from a single farm a few miles from the malt house, plump and bright after a germination schedule built for them. Breakfast cereal, oatmeal cookie and a hint of vanilla, and the silk that oats give a stout or a hazy.',
    tags: ['oat', 'malted-oats', 'mouthfeel', 'hazy', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/adjunct-malts/malted-oats',
  }),
  craftMalt({
    id: 'carolina-malt-malted-corn',
    name: 'Malted Corn',
    maker: 'Carolina Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 2,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'North Carolina yellow dent corn from Tidewater Grain Company, malted and fully gelatinised so no cereal mash is needed. Sweet and faintly savory — corn pudding rather than the neutrality of flaked maize.',
    tags: ['adjunct', 'corn', 'maize', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/adjunct-malts/malted-corn',
  }),
  craftMalt({
    id: 'carolina-malt-unmalted-wheat',
    name: 'Unmalted Wheat',
    maker: 'Carolina Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 76,
    moisturePercent: 10,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    description:
      'Raw wheat from the same grower network, unmalted and full of protein. Up to about 15 % builds body, foam and haze in a witbier or a hazy IPA, and it is the grain to reach for in a turbid mash.',
    tags: ['wheat', 'unmalted', 'adjunct', 'witbier', 'hazy', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/brewing-adjuncts/unmalted-wheat',
  }),
  craftMalt({
    id: 'carolina-malt-biscuit',
    name: 'Biscuit',
    maker: 'Carolina Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73,
    moisturePercent: 4,
    colorLovibond: 25,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    description:
      'Warm baguette and the toasty underside of a cookie, gentle enough that a few percent seasons a pale lager without darkening it much. Pushed towards the 15 % ceiling it layers real complexity into an English ale or a stout.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/roasted-line/biscuit',
  }),
  craftMalt({
    id: 'carolina-malt-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Carolina Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 480,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Espresso and cocoa powder with the sharp bitterness roasted barley is bought for, plus the dark tan head it builds. Five to eight percent makes an Irish stout; one or two darkens a schwarzbier.',
    tags: ['roasted', 'barley', 'stout', 'irish', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/roasted-line/roasted-barley',
  }),
  craftMalt({
    id: 'carolina-malt-sunset-wheat',
    name: 'Sunset Wheat',
    maker: 'Carolina Malt House',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 425,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 6,
    needsMashing: false,
    description:
      'A custom roast pitched between chocolate wheat and a debittered black malt: cocoa powder and light roast coffee with none of the husky bite, because the wheat has no husk. Color and smoothness for a big stout, a brown ale or a dark Czech lager.',
    tags: ['roasted', 'wheat', 'debittered', 'smooth', 'craft-malt', 'north-carolina'],
    url: 'https://www.carolinamalt.com/portfolio-collections/roasted-line/sunset-wheat',
  }),
  craftMalt({
    id: 'carolina-malt-malted-rice',
    name: 'Malted Rice',
    maker: 'Carolina Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 6,
    colorLovibond: 2.5,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'Heirloom rice grown on the North Carolina coast by Tidewater Grain Company. More flavor than flaked rice, a crisp dry finish, and no cereal mash required.',
    tags: ['adjunct', 'rice', 'heirloom', 'craft-malt', 'north-carolina', 'light-body'],
    url: 'https://www.carolinamalt.com/portfolio-collections/adjunct-malts/malted-rice',
  }),

  // Added post-merger: on the combined portfolio, not on Carolina Malt's own
  // fifteen. Filed under Carolina Malt because the grain is North Carolina's.
  craftMalt({
    id: 'carolina-malt-seashore-black-rye',
    name: 'Seashore Black Rye',
    maker: 'Carolina Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.75,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'An heirloom rye off the mineral-rich Blacklands soil of Pamlico County, and it tastes nothing like the peppery rye brewers expect. Liquorice and dried fruit with a natural sweetness and a coffee-like richness underneath. Sticky in the lauter past a quarter of the grist.',
    tags: ['base', 'rye', 'heirloom', 'craft-malt', 'north-carolina', 'local'],
  }),

  // ---------------------------------------------------------------------------
  // Epiphany Craft Malt — Durham, North Carolina
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'epiphany-pale-malt',
    name: 'Pale Malt',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Everyday pale base malt from North Carolina barley, floor-influenced and kilned gently. Rounder and grainier than a large-scale two-row, which shows most clearly in beers with a short hop bill.',
    tags: ['base', 'ale', 'craft-malt', 'north-carolina', 'local'],
  }),
  craftMalt({
    id: 'epiphany-wadehelles',
    name: 'WadeHelles',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Low-kiln winter two-row, cured cool so DMS precursor stays low and the malt tastes of fresh toast, honeysuckle and melon rind. The house lager and pale-ale canvas.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/modern-pilsner-2/',
  }),
  craftMalt({
    id: 'epiphany-munich',
    name: 'Munich Malt',
    maker: 'Epiphany Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 35,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Kilned to a mid Munich color with enough enzyme left to convert itself in a well-balanced grist. Adds bread-crust depth and an amber tone to festbiers, dunkels and darker American ales.',
    tags: ['kilned', 'munich', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/munich/',
  }),
  craftMalt({
    id: 'epiphany-winter-wheat',
    name: 'Winter Wheat',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Soft red winter wheat from a nearby family farm, germinated short so protein and mouthfeel survive. Honey-cracker and whole-grain loaf; rice hulls past about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'north-carolina', 'head-retention'],
    url: 'https://www.epiphanymalt.com/product/wheat/',
  }),
  craftMalt({
    id: 'epiphany-triticale',
    name: 'Triticale',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Malted rye–wheat hybrid with a fruity caramel note and wheat mouthfeel. A distinctive 10–30 % seasoning in farmhouse ales and anything that wants spice without a full rye mash.',
    tags: ['base', 'triticale', 'craft-malt', 'north-carolina', 'spicy'],
    url: 'https://www.epiphanymalt.com/product/triticale/',
  }),
  craftMalt({
    id: 'epiphany-candy-40',
    name: 'Candy 40',
    maker: 'Epiphany Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5.5,
    // The name says 40; the published range is 35–50, so the mid-point wins.
    colorLovibond: 42,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Mid-light crystal from Carolina barley: honey and caramel without the raisin depth of darker grades. Five to ten percent rounds a pale ale or ambers a lager.',
    tags: ['crystal', 'caramel', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/percolator-2/',
  }),
  craftMalt({
    id: 'epiphany-icarus',
    name: 'Icarus',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    // 480–525 on Epiphany's own range; this sat at 400 before anyone checked.
    colorLovibond: 500,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'House roast taken right up to the edge — dark cocoa and coffee with a little burnt-sugar bitterness. Two to six percent in stout and porter is plenty; more and the beer turns ashy.',
    tags: ['roasted', 'chocolate', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/icarus/',
  }),
  craftMalt({
    id: 'epiphany-pecan-smoked',
    name: 'Pecan Smoked Malt',
    maker: 'Epiphany Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Base malt smoked over pecan, one of the woods in the house smoked line. Sweeter and nuttier than beech, so it can carry a smoked lager or season a porter at 5–20 %.',
    tags: ['smoked', 'pecan', 'craft-malt', 'north-carolina', 'base'],
  }),
  craftMalt({
    id: 'epiphany-century',
    name: 'Century',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Moderately modified pilsner malt from North Carolina winter barley. The least modified of the low-kiln range, so it rewards a step mash and gives a fuller, maltier pilsner than a fully modified continental base.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/modern-pilsner-2-2/',
  }),
  craftMalt({
    id: 'epiphany-foundation',
    name: 'Foundation',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3,
    diastaticPowerLintner: 75,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'The house workhorse: well-modified two-row winter barley, kilned for fresh toast, honeysuckle and melon rind. A single-infusion base that will carry any pale beer.',
    tags: ['base', 'ale', 'pale', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/foundation/',
  }),
  craftMalt({
    id: 'epiphany-modern',
    name: 'Modern',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Well-modified pilsner malt for brewers who want the Carolina terroir without the step mash Century asks for. Clean, bready and high in enzyme.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/modern-pilsner/',
  }),
  craftMalt({
    id: 'epiphany-rye',
    name: 'Rye',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 77,
    moisturePercent: 5,
    colorLovibond: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Malted Seashore rye, a landrace baking-flour variety rather than a modern brewing rye. Peppery and dry with the slick body rye always brings; it needs rice hulls above about a quarter of the grist.',
    tags: ['rye', 'landrace', 'alternative', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/rye/',
  }),
  craftMalt({
    id: 'epiphany-tidewater-gold-rice',
    name: 'Tidewater Gold Rice',
    maker: 'Epiphany Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 80,
    moisturePercent: 10,
    colorLovibond: 1.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'A Madagascar-origin landrace rice grown in North Carolina and malted rather than flaked. Lighter and cleaner than flaked rice, with a faint floral note the commodity product does not have.',
    tags: ['rice', 'adjunct', 'landrace', 'gluten-free', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/tidewater-gold-rice/',
  }),
  craftMalt({
    id: 'epiphany-tuxpeno-corn',
    name: 'Tuxpeño Corn',
    maker: 'Epiphany Craft Malt',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 78,
    moisturePercent: 10,
    colorLovibond: 2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 30,
    needsMashing: true,
    description:
      'An ancient non-GMO corn variety, organically grown and malted. Sweet and distinctly of corn in a way flaked maize is not, which is the point of using it.',
    tags: ['corn', 'maize', 'adjunct', 'heirloom', 'gluten-free', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/tuxpeno-corn/',
  }),
  craftMalt({
    id: 'epiphany-candy-100',
    name: 'Candy 100',
    maker: 'Epiphany Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 5.5,
    colorLovibond: 90,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'The dark half of the house crystal pair, made by roasting green malt rather than kilned malt. Raisin, dark toffee and a little stone fruit, without the sharpness a conventional dark crystal can carry.',
    tags: ['crystal', 'caramel', 'dark', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/candy-100/',
  }),
  craftMalt({
    id: 'epiphany-wadefest',
    name: 'WadeFest',
    maker: 'Epiphany Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'The seasonal festmalt: a shade darker than WadeHelles and rounder with it, built for märzen and autumn lagers. Seasonal, so it is not always available.',
    tags: ['kilned', 'festbier', 'marzen', 'seasonal', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/vienna-2/',
  }),
  craftMalt({
    id: 'epiphany-vienna',
    name: 'Vienna',
    maker: 'Epiphany Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Carolina Vienna: light toast and honey over a still-substantial enzyme load, so it can be most of a grist. Warmer and breadier than the low-kiln malts without going to Munich depth.',
    tags: ['kilned', 'vienna', 'lager', 'amber', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/vienna/',
  }),
  craftMalt({
    id: 'epiphany-bruehmalt',
    name: 'Brüehmalt',
    maker: 'Epiphany Craft Malt',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 8,
    diastaticPowerLintner: 35,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'A honey-style malt made by stewing before the kiln, which builds sweetness rather than toast. Sits between Vienna and Munich in color and well past both in perceived sweetness.',
    tags: ['kilned', 'honey', 'malty', 'amber', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/bruehmalt/',
  }),
  craftMalt({
    id: 'epiphany-olfaction',
    name: 'Olfaction',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 4,
    colorLovibond: 35,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'An aromatic-style roast taken only far enough to build aroma: bread crust, cocoa nib and toasted nut with almost none of the astringency a darker roast brings. Use it where you want the smell of a dark beer and not the bite.',
    tags: ['roasted', 'aromatic', 'light-roast', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/olfaction/',
  }),
  craftMalt({
    id: 'epiphany-biscuit',
    name: 'Biscuit',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73,
    moisturePercent: 4,
    colorLovibond: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Biscuit-style roast: digestive biscuit, toasted crust and a dry finish. Five to ten percent gives a pale ale a bakery note without moving the color much.',
    tags: ['roasted', 'biscuit', 'toasted', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/brewers-breakfast/',
  }),
  craftMalt({
    id: 'epiphany-ruby',
    name: 'Ruby',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Color without roast character, which is the whole brief: it takes a beer to deep red or brown while contributing very little of the acrid edge a chocolate malt would. The way to darken an amber or a dunkel honestly.',
    tags: ['roasted', 'red', 'colour', 'dunkel', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/ruby/',
  }),
  craftMalt({
    id: 'epiphany-perculator',
    name: 'Perculator',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 275,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Coffee-style roast, and it reads as coffee rather than as chocolate: fresh grounds and a clean bitter finish. Two to five percent in a porter, or more if coffee is the point.',
    tags: ['roasted', 'coffee', 'porter', 'stout', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/percolator/',
  }),
  craftMalt({
    id: 'epiphany-bittersweet',
    name: 'Bittersweet',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4,
    colorLovibond: 385,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    description:
      'Chocolate-style roast, rich and dark with the bittersweet edge the name promises. The house chocolate malt, and the one to reach for before Icarus in a porter.',
    tags: ['roasted', 'chocolate', 'porter', 'stout', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/bittersweet/',
  }),
  craftMalt({
    id: 'epiphany-smooth',
    name: 'Smooth',
    maker: 'Epiphany Craft Malt',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4,
    colorLovibond: 540,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    description:
      'Black wheat malt: as dark as anything in the range and, because it is wheat and huskless, far smoother than a black barley malt. Color and dry roast for a stout without the astringency.',
    tags: ['roasted', 'black', 'wheat', 'stout', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/smooth-operator/',
  }),
  craftMalt({
    id: 'epiphany-candykrush',
    name: 'CandyKrush',
    maker: 'Epiphany Craft Malt',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 10,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 25,
    needsMashing: false,
    description:
      'A caramel-dextrin malt from the Maltoir line: light in color but heavy in unfermentable dextrin, so it builds body and head rather than sweetness or color. Useful anywhere a beer reads thin.',
    tags: ['crystal', 'dextrin', 'body', 'mouthfeel', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/candykrush/',
  }),
  craftMalt({
    id: 'epiphany-modifier',
    name: 'Modifier',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1,
    diastaticPowerLintner: 60,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Chit malt: barley taken only a day or two into germination, so the starch is barely modified and the protein is largely intact. Added for head retention and body, not for extract.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/modifier/',
  }),
  craftMalt({
    id: 'epiphany-emulator',
    name: 'Emulator',
    maker: 'Epiphany Craft Malt',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'A continental lager booster: pilsner malt with a whisper of applewood smoke, low enough that it reads as depth rather than as a smoked beer. Built to give an American lager the weight of a German one.',
    tags: ['base', 'pilsner', 'lager', 'smoked', 'applewood', 'craft-malt', 'north-carolina'],
    url: 'https://www.epiphanymalt.com/product/rye-2/',
  }),

  // ---------------------------------------------------------------------------
  // Riverbend Malt House — Asheville, North Carolina
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'riverbend-southern-select-pilsner',
    name: 'Southern Select',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    // Riverbend publish 2.5–3.5 SRM; 2.4 sat just under the range.
    colorLovibond: 3,
    diastaticPowerLintner: 80,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Flagship pale from a blend of southern Appalachian 2-row, formerly Avalon Pale. Biscuit, fresh grass and honeysuckle; extract runs a little below commodity 2-row, so expect to adjust the grist weight.',
    tags: ['base', 'ale', 'craft-malt', 'north-carolina', 'local'],
    url: 'https://riverbendmalt.com/malt/southern-select/',
  }),
  craftMalt({
    id: 'riverbend-base-camp',
    name: 'Base Camp',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Extra-pale 2-row with a clean, grassy flavor. Built for hop-forward styles that want a quiet malt floor without dropping all the way to a pilsner color.',
    tags: ['base', 'pale', 'craft-malt', 'north-carolina', 'ipa'],
    url: 'https://riverbendmalt.com/malt/base-camp/',
  }),
  craftMalt({
    id: 'riverbend-chesapeake-pilsner',
    name: 'Chesapeake Pilsner',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    // Published 1.8–2.3 SRM; 1.7 sat just under it.
    colorLovibond: 2,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Virginia-grown 2-row pilsner with bread-crumb sweetness, a green-tea finish and enough enzyme to convert itself. The hop-forward lager base in the Riverbend line.',
    tags: ['base', 'pilsner', 'craft-malt', 'north-carolina', 'virginia'],
    url: 'https://riverbendmalt.com/malt/chesapeake-pilsner/',
  }),
  craftMalt({
    id: 'riverbend-appalachian-wheat',
    name: 'Appalachian Wheat',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    // Published 3.0–5.0 SRM; this was carrying 2.2, well below the range.
    colorLovibond: 4,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 60,
    needsMashing: true,
    description:
      'Malted soft Oakes wheat from regional growers, husk-free and high in protein. Builds foam and a silky body in wheat beers and saisons; rice hulls help the lauter run once it passes about a third of the grist.',
    tags: ['base', 'wheat', 'craft-malt', 'north-carolina', 'head-retention'],
    url: 'https://riverbendmalt.com/malt/appalachian-wheat/',
  }),
  craftMalt({
    id: 'riverbend-light-munich',
    name: 'Light Munich',
    maker: 'Riverbend Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    // Published 8–12 SRM; the mid-point rather than the floor.
    colorLovibond: 10,
    diastaticPowerLintner: 40,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'Southern 2-row kilned to fresh-bread and honey color. Still self-converting, so it can carry a helles bock or season an American single-malt ale.',
    tags: ['kilned', 'munich', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/light-munich/',
  }),
  craftMalt({
    id: 'riverbend-crystal-50',
    name: 'Crystal 50',
    maker: 'Riverbend Malt House',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    colorLovibond: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    description:
      'Mid-color crystal from Appalachian barley: caramel sweetness and a copper cast without the raisin depth of an 80 L. Five to ten percent in amber and brown ales.',
    tags: ['crystal', 'caramel', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/crystal-malt/',
  }),
  // Riverbend announced in June 2024 that they were phasing 6-row barley out by
  // the end of that year, and it is gone from both their malt list and their
  // shop. Kept so recipes that snapshot the id still resolve.
  craftMalt({
    id: 'riverbend-six-row-pilsner',
    name: '6-Row Pilsner',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 140,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Heritage six-row pilsner with grain and honey notes and the extra enzyme pre-Prohibition adjunct beers were built on. A natural base for Kentucky common, cream ale and mixed-culture saison.',
    tags: ['base', 'pilsner', 'six-row', 'craft-malt', 'north-carolina', 'high-enzyme', 'heritage'],
  }),
  craftMalt({
    id: 'riverbend-carolina-rye',
    name: 'Carolina Rye',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    // Published 5–8 SRM; this was carrying 3.5.
    colorLovibond: 6.5,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 50,
    needsMashing: true,
    description:
      'Wrens Abruzzi rye with a spicy character and a hint of black pepper. Sticky mash above about 20 %; rice hulls are the difference between a rye beer and a stuck sparge.',
    tags: ['base', 'rye', 'craft-malt', 'north-carolina', 'spicy', 'heritage'],
    url: 'https://riverbendmalt.com/malt/carolina-rye/',
  }),
  craftMalt({
    id: 'riverbend-bloody-butcher-corn',
    name: 'Bloody Butcher Corn',
    maker: 'Riverbend Malt House',
    type: 'adjunct-grain',
    maltClass: 'non-buffering',
    extractFineDryPercent: 75,
    moisturePercent: 5,
    // Published 1.5–2.5 SRM. Riverbend also publish a minimum extract of 40 %,
    // which is far below anything the catalog models; extract stays the
    // class-typical figure the `representative` provenance claims.
    colorLovibond: 2,
    diastaticPowerLintner: 20,
    suggestedMaxPercent: 40,
    needsMashing: true,
    description:
      'Malted heirloom red corn with a chai-spice note. A flavorful adjunct for cream ale, pre-Prohibition lager and bourbon mash bills that want southern grain rather than flakes.',
    tags: ['adjunct', 'corn', 'heirloom', 'craft-malt', 'north-carolina', 'pre-prohibition'],
    url: 'https://riverbendmalt.com/malt/bloody-butcher-corn/',
  }),
  craftMalt({
    id: 'riverbend-honey-malt',
    name: 'Honey Malt',
    maker: 'Riverbend Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 76,
    moisturePercent: 4.5,
    colorLovibond: 20,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Stewed then kilned like a melanoidin malt, giving honeycomb sweetness and bread-crust aroma without crystal stickiness. Five to fifteen percent in amber ales and malt-forward lagers.',
    tags: ['kilned', 'honey', 'melanoidin', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/honey-malt/',
  }),
  craftMalt({
    id: 'riverbend-cumberland-pilsner',
    name: 'Cumberland Pilsner',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 85,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Formerly Avalon Pilsner: fruit and sweet bread over a floral finish. The house lager base, and a softer, more aromatic pilsner malt than a continental one.',
    tags: ['base', 'pilsner', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/cumberland-pilsner/',
  }),
  craftMalt({
    id: 'riverbend-czech-style-pilsner',
    name: 'Czech-Style Pilsner',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 77,
    moisturePercent: 4.5,
    colorLovibond: 2,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    description:
      'Deliberately under-modified in the Czech manner, so it wants a step mash rather than a single infusion. Grainy sweetness, cooked rice and pear, finishing on hay and wildflower.',
    tags: ['base', 'pilsner', 'czech', 'under-modified', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/czech-style-pilsner/',
  }),
  craftMalt({
    id: 'riverbend-vienna',
    name: 'Vienna',
    maker: 'Riverbend Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 4,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 80,
    needsMashing: true,
    description:
      'A proprietary two-row blend kilned richer and slightly darker than Southern Select. Toast and light honey with enough enzyme to carry most of a grist.',
    tags: ['kilned', 'vienna', 'amber', 'lager', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/vienna/',
  }),
  craftMalt({
    id: 'riverbend-double-kilned-munich',
    name: 'Double Kilned Munich',
    maker: 'Riverbend Malt House',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 50,
    diastaticPowerLintner: 15,
    suggestedMaxPercent: 25,
    needsMashing: true,
    description:
      'Taken through the kiln twice, which is why it lands near 50 SRM rather than the usual Munich range: dark fruit and toffee with roasted coffee behind it. A color and depth malt rather than a base.',
    tags: ['kilned', 'munich', 'dark', 'toffee', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/double-kilned-munich/',
  }),
  craftMalt({
    id: 'riverbend-great-chit',
    name: 'Great Chit',
    maker: 'Riverbend Malt House',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 72,
    moisturePercent: 6,
    colorLovibond: 1.5,
    diastaticPowerLintner: 55,
    suggestedMaxPercent: 20,
    needsMashing: true,
    description:
      'Under-modified Southern two-row stopped early in germination. Added for foam stability and mouthfeel rather than for extract, which is why the suggested proportion is small.',
    tags: ['chit', 'head-retention', 'body', 'protein', 'craft-malt', 'north-carolina'],
    url: 'https://riverbendmalt.com/malt/great-chit/',
  }),

  // ---------------------------------------------------------------------------
  // South Fork Malthouse — Cynthiana, Kentucky
  //
  // `provenance: 'spec-sheet'` is earned here, with one exception worth naming.
  // South Fork publish a full analysis per malt on `southforkmalthouse.com/malts`
  // — colour, fine-grind extract, protein, diastatic power and alpha amylase —
  // and every figure below is theirs, unrounded: 1900 at 1.6 SRM / 79.3 % / 146,
  // Sunrise at 1.8 / 81.3 / 185, Munich at 17 / 80.1 / 79, Bourbon County at
  // 1.6 / 81.2 / 200.
  //
  // **Moisture is the exception.** They publish none for any of the four, so the
  // 4–4.5 % below is class-typical, and `provenance` has no way to say
  // "spec-sheet except one field". Moisture only trims the derived PPG by a few
  // tenths, so the label is more true than false — but it is not wholly true,
  // and this comment is where that is recorded.
  //
  // 1900 Pilsner really is six-row: "the first offering in our planned 1900
  // series of pre-prohibition malts featuring era appropriate, six-row barley".
  // Sunrise and Bourbon County are their 2-row, as their own copy says.
  // ---------------------------------------------------------------------------
  craftMalt({
    id: 'south-fork-1900-pilsner',
    name: '1900 Pilsner',
    maker: 'South Fork Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 79.3,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 146,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Pre-Prohibition six-row from Kentucky barley: slightly hazy wort, straw and grain, and enough enzyme to convert a serious adjunct fraction. Built for Kentucky common, cream ale and hazy IPA.',
    tags: [
      'base',
      'pilsner',
      'six-row',
      'craft-malt',
      'kentucky',
      'high-enzyme',
      'pre-prohibition',
    ],
  }),
  craftMalt({
    id: 'south-fork-sunrise-pilsner',
    name: 'Sunrise Pilsner',
    maker: 'South Fork Malthouse',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 81.3,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    diastaticPowerLintner: 185,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'European-style 2-row with honey, malt and a grassy note, and diastatic power high enough for heavy adjunct or high-gravity brewing. The clean lager base of the house.',
    tags: ['base', 'pilsner', 'craft-malt', 'kentucky', 'high-enzyme', 'two-row'],
  }),
  craftMalt({
    id: 'south-fork-munich',
    name: 'Munich Malt',
    maker: 'South Fork Malthouse',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80.1,
    moisturePercent: 4,
    colorLovibond: 17,
    diastaticPowerLintner: 79,
    suggestedMaxPercent: 60,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'German-style 2-row Munich with rich malt, a hint of chocolate and caramel, and more enzyme left than most dark Munichs. Built for märzen, bock and anything that needs color and aroma.',
    tags: ['kilned', 'munich', 'craft-malt', 'kentucky'],
  }),
  craftMalt({
    id: 'south-fork-bourbon-county-distillers',
    name: 'Bourbon County Distillers',
    maker: 'South Fork Malthouse',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 81.2,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    diastaticPowerLintner: 200,
    suggestedMaxPercent: 100,
    needsMashing: true,
    provenance: 'spec-sheet',
    description:
      'Lightly kilned Kentucky 2-row with mild honey-malt flavor and diastatic power aimed at unmalted corn and rye. The local conversion malt for bourbon mash bills.',
    tags: ['base', 'distillers', 'craft-malt', 'kentucky', 'high-enzyme'],
  }),
];
